import { useState, useMemo, ImgHTMLAttributes } from "react";

const PROXIED_HOSTS = new Set<string>([
  "alsassodistria.it",
  "www.alsassodistria.it",
]);

const WIDTHS = [400, 800, 1200, 1600];
const PROXY_BASE = "https://images.weserv.nl/";

function shouldProxy(src: string) {
  try {
    return PROXIED_HOSTS.has(new URL(src).hostname);
  } catch {
    return false;
  }
}

function proxied(src: string, width?: number) {
  const stripped = src.replace(/^https?:\/\//, "");
  const params = new URLSearchParams({ url: stripped });
  if (width) {
    params.set("w", String(width));
    params.set("we", "");
  }
  return `${PROXY_BASE}?${params.toString()}`;
}

function buildSrcSet(src: string) {
  return WIDTHS.map((w) => `${proxied(src, w)} ${w}w`).join(", ");
}

interface Props extends Omit<ImgHTMLAttributes<HTMLImageElement>, "srcSet"> {
  src: string;
}

function decodeEntities(s: string) {
  let prev = s;
  for (let i = 0; i < 3; i++) {
    const next = prev
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'")
      .replace(/&#34;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, "&");
    if (next === prev) break;
    prev = next;
  }
  return prev;
}

function sanitizeUrl(src: string) {
  return decodeEntities(src).trim().replace(/^["'\s]+|["'\s]+$/g, "");
}

export default function ResponsiveImage({
  src,
  alt = "",
  onError,
  ...rest
}: Props) {
  const [errored, setErrored] = useState(false);

  const cleanSrc = useMemo(() => sanitizeUrl(src), [src]);
  const useProxy = useMemo(
    () => !errored && shouldProxy(cleanSrc),
    [cleanSrc, errored]
  );

  return (
    <img
      {...rest}
      src={useProxy ? proxied(cleanSrc, 1200) : cleanSrc}
      alt={alt}
      srcSet={useProxy ? buildSrcSet(cleanSrc) : undefined}
      sizes={useProxy ? "(max-width: 1200px) 100vw, 1200px" : undefined}
      loading="lazy"
      referrerPolicy={useProxy ? "no-referrer" : undefined}
      onError={(e) => {
        if (!errored) setErrored(true);
        onError?.(e);
      }}
    />
  );
}
